const BACKGROUND_CLASS = "global-bg-padding";
const WRAPPER_CLASS = "stacked-background";
const CONTAINER_CLASS = "global-content-stack";
const LEADING_BACKGROUND_CLASS = "has-leading-background";
const ENDING_BACKGROUND_CLASS = "has-ending-background";
const BLOCK_SELECTOR = "[data-nav-item]";

type EdgeTarget = {
    element: HTMLElement;
    isBlockElement: boolean;
};

const findFirstVisibleDescendant = (container: HTMLElement): HTMLElement | null => {
    const walker = document.createTreeWalker(container, NodeFilter.SHOW_ELEMENT, {
        acceptNode: (node) => {
            const element = node as HTMLElement;
            const style = window.getComputedStyle(element);

            if (style.display === "none") {
                return NodeFilter.FILTER_REJECT; // Prune subtree
            }

            if (style.visibility === "hidden") {
                return NodeFilter.FILTER_SKIP; // Skip this node, check children
            }

            // Check dimensions
            const rect = element.getBoundingClientRect();
            if (rect.width > 0 || rect.height > 0) {
                return NodeFilter.FILTER_ACCEPT;
            }

            return NodeFilter.FILTER_SKIP;
        }
    });

    return walker.nextNode() as HTMLElement | null;
};

const findLastVisibleDescendant = (container: HTMLElement): HTMLElement | null => {
    const children = container.children;
    for (let i = children.length - 1; i >= 0; i--) {
        const child = children[i] as HTMLElement;
        const style = window.getComputedStyle(child);

        if (style.display === "none") {
            continue; // Prune subtree
        }

        // Search descendants first (deepest last)
        const descendant = findLastVisibleDescendant(child);
        if (descendant) {
            return descendant;
        }

        // Check child itself
        if (style.visibility !== "hidden") {
            const rect = child.getBoundingClientRect();
            if (rect.width > 0 || rect.height > 0) {
                return child;
            }
        }
    }
    return null;
};

const findVisibleDescendant = (container: HTMLElement, searchFromEnd: boolean): HTMLElement | null => {
    if (searchFromEnd) {
        return findLastVisibleDescendant(container);
    }
    return findFirstVisibleDescendant(container);
};

const findEdgeTarget = (container: HTMLElement, searchFromEnd: boolean): EdgeTarget | null => {
    const blockElements = Array.from(container.querySelectorAll<HTMLElement>(BLOCK_SELECTOR));

    if (blockElements.length > 0) {
        const element = searchFromEnd ? blockElements[blockElements.length - 1] : blockElements[0];
        return { element, isBlockElement: true };
    }

    const fallback = findVisibleDescendant(container, searchFromEnd);
    if (fallback) {
        return { element: fallback, isBlockElement: false };
    }

    const directChild = searchFromEnd ? container.lastElementChild : container.firstElementChild;
    if (directChild instanceof HTMLElement) {
        return { element: directChild, isBlockElement: false };
    }

    return null;
};

const hasBackgroundWithinHierarchy = (element: HTMLElement, container: HTMLElement): boolean => {
    let current: HTMLElement | null = element;

    while (current && current !== container) {
        if (current.classList.contains(BACKGROUND_CLASS)) {
            return true;
        }

        current = current.parentElement;
    }

    return false;
};

export default function initBackgroundStack(): void {
    const elementContainsBackground = (element: HTMLElement): boolean => {
        if (element.classList.contains(BACKGROUND_CLASS)) {
            return true;
        }

        return element.querySelector(`.${BACKGROUND_CLASS}`) !== null;
    };

    const checkContainerEdgeBackground = (container: HTMLElement, searchFromEnd: boolean): boolean => {
        const target = findEdgeTarget(container, searchFromEnd);

        if (!target) {
            return false;
        }

        return hasBackgroundWithinHierarchy(target.element, container);
    };

    const containerEndsWithBackground = (container: HTMLElement): boolean => {
        return checkContainerEdgeBackground(container, true);
    };

    const containerStartsWithBackground = (container: HTMLElement): boolean => {
        return checkContainerEdgeBackground(container, false);
    };

    const updateBackgroundEdgeState = (container: HTMLElement): void => {
        if (!container.classList.contains(CONTAINER_CLASS)) {
            return;
        }

        if (containerEndsWithBackground(container)) {
            container.classList.add(ENDING_BACKGROUND_CLASS);
        } else {
            container.classList.remove(ENDING_BACKGROUND_CLASS);
        }

        if (containerStartsWithBackground(container)) {
            container.classList.add(LEADING_BACKGROUND_CLASS);
        } else {
            container.classList.remove(LEADING_BACKGROUND_CLASS);
        }
    };

    const wrapConsecutiveBackgrounds = (container: HTMLElement): void => {
        const children = Array.from(container.children) as HTMLElement[];
        const groups: HTMLElement[][] = [];
        let currentGroup: HTMLElement[] = [];

        const flushGroup = (): void => {
            if (currentGroup.length > 1) {
                groups.push([...currentGroup]);
            }

            currentGroup = [];
        };

        children.forEach((child) => {
            if (!(child instanceof HTMLElement)) {
                flushGroup();
                return;
            }

            if (child.classList.contains(WRAPPER_CLASS)) {
                flushGroup();
                return;
            }

            if (elementContainsBackground(child)) {
                currentGroup.push(child);
            } else {
                flushGroup();
            }
        });

        flushGroup();

        groups.forEach((group) => {
            const wrapper = document.createElement("div");
            wrapper.classList.add(WRAPPER_CLASS);

            const referenceNode = group[0];
            container.insertBefore(wrapper, referenceNode);

            group.forEach((child) => {
                wrapper.appendChild(child);
            });
        });

        updateBackgroundEdgeState(container);
    };

    const stackBackgroundBlocks = (): void => {
        const backgrounds = document.querySelectorAll<HTMLElement>(`.${BACKGROUND_CLASS}`);
        const containers = new Set<HTMLElement>();

        backgrounds.forEach((background) => {
            let current: HTMLElement | null = background.parentElement;

            while (current instanceof HTMLElement) {
                containers.add(current);
                current = current.parentElement;
            }
        });

        containers.forEach((container) => {
            wrapConsecutiveBackgrounds(container);
            updateBackgroundEdgeState(container);
        });
    };

    if (document.readyState === "loading") {
        document.addEventListener("DOMContentLoaded", stackBackgroundBlocks, { once: true });
    } else {
        stackBackgroundBlocks();
    }
}
